import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { ShoppingBag } from "lucide-react";
import { shopifyImg, shopifySrcSet, SIZES_GRID_4 } from "@/lib/format";

const STORAGE_KEY = "aerwear_recently_viewed";
const MAX_ITEMS = 8;

export function trackRecentlyViewed(product) {
  if (!product?.id) return;
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    const filtered = stored.filter((p) => p.id !== product.id);
    const entry = {
      id: product.id,
      name: product.name,
      price: product.price,
      compare_price: product.compare_price,
      image: product.images?.[0] || "",
      gender: product.gender,
      category: product.category,
      colors: product.colors,
      variants: product.variants,
      sizes: product.sizes,
      timestamp: Date.now(),
    };
    const updated = [entry, ...filtered].slice(0, MAX_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (err) { if (process.env.NODE_ENV === 'development') console.warn('Save recently viewed:', err.message); }
}

export function getRecentlyViewed() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch (err) {
    return [];
  }
}

export default function RecentlyViewed({ currentProductId }) {
  const [items, setItems] = useState([]);
  const { addItem } = useCart();

  useEffect(() => {
    const all = getRecentlyViewed();
    const filtered = currentProductId
      ? all.filter((p) => p.id !== currentProductId)
      : all;
    setItems(filtered.slice(0, 4));
  }, [currentProductId]);

  if (items.length === 0) return null;

  return (
    <section data-testid="recently-viewed" className="py-20 px-6 md:px-12 lg:px-20">
      <div className="mb-10">
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/15 font-['Inter'] mb-3">Recently Viewed</p>
        <h2 className="text-2xl md:text-3xl font-['Bebas_Neue'] tracking-tight text-white">
          Pick Up Where You Left Off
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {items.map((item) => {
          const defaultVariant = item.variants?.find((v) => v.available) || item.variants?.[0];
          return (
            <div key={item.id} data-testid={`recent-${item.id}`} className="group relative">
              <Link to={`/products/${item.id}`}>
                <div className="aspect-[3/4] overflow-hidden bg-[#080808] mb-3">
                  {item.image ? (
                    <img src={shopifyImg(item.image, 720)} srcSet={shopifySrcSet(item.image)} sizes={SIZES_GRID_4} alt={item.name} width="720" height="960" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-80 group-hover:opacity-100" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-white/15">
                      <ShoppingBag size={32} strokeWidth={1} />
                    </div>
                  )}
                </div>
              </Link>
              <button
                data-testid={`recent-quick-add-${item.id}`}
                onClick={() => {
                  const v = defaultVariant;
                  addItem(item, v?.size || item.sizes?.[0] || "", v?.color || item.colors?.[0]?.name || "", 1, v?.id || null);
                }}
                className="opacity-0 group-hover:opacity-100 absolute bottom-[48px] left-0 right-0 bg-white text-black py-3 text-[10px] uppercase tracking-[0.25em] font-semibold transition-all duration-300"
              >
                Quick Add
              </button>
              <div>
                <Link to={`/products/${item.id}`}>
                  <h3 className="text-[10px] uppercase tracking-[0.1em] font-['Inter'] text-white/50 truncate">{item.name}</h3>
                </Link>
                <span className="text-sm font-['Inter'] font-light text-white/30 mt-1 block">${item.price?.toFixed(2)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
